import type { PublicSupplier } from '@tandemone/types'
import { useTheme } from 'next-themes'
import { useEffect, useMemo, useRef, useState } from 'react'

import { loadGoogleLibrary } from '@/lib/google-maps-loader'

import { mountSupplierMap, parseCoord, resolveMapId } from './map'
import { ProfileEmpty } from './profile-empty'
import { ProfileSection } from './profile-section'

type Props = {
  companyName: string
  supplier: PublicSupplier
}

const INITIAL_ZOOM = 14

export function ProfileMap({ companyName, supplier }: Props) {
  const title = `${companyName} HQ Location`
  /** memoized so its identity only changes when the coords do, letting the
   * map effect depend on the object directly without re-running per render.
   */
  const hq = useMemo(
    () => parseCoord(supplier.latitude, supplier.longitude),
    [supplier.latitude, supplier.longitude]
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>(
    'loading'
  )
  const { resolvedTheme } = useTheme()
  const mapId = resolveMapId()

  useEffect(() => {
    if (!hq) {
      setStatus('unavailable')
      return
    }
    let cancelled = false
    void Promise.all([
      loadGoogleLibrary('maps'),
      loadGoogleLibrary('marker')
    ]).then(([mapsLoaded, markerLoaded]) => {
      if (cancelled) return
      if (!mapsLoaded || !markerLoaded || !containerRef.current) {
        setStatus('unavailable')
        return
      }
      /** google maps writes a lot of internal dom into the container and
       * doesn't expose a destroy api. wiping innerHTML on each (re)mount
       * keeps a theme toggle from stacking a second map on top of the
       * first. colorScheme can only be set at construction, so a fresh
       * map is required when the theme changes.
       */
      containerRef.current.innerHTML = ''
      mountSupplierMap(containerRef.current, hq, {
        mapId,
        isDark: resolvedTheme === 'dark',
        zoom: INITIAL_ZOOM
      })
      setStatus('ready')
    })
    return () => {
      cancelled = true
    }
  }, [hq, mapId, resolvedTheme])

  if (!supplier.location && !hq) {
    return (
      <ProfileSection title={title}>
        <ProfileEmpty message='No headquarters address on file.' />
      </ProfileSection>
    )
  }

  return (
    <ProfileSection title={title}>
      <div className='flex flex-col gap-3'>
        {supplier.location ? (
          <p className='text-sm text-foreground'>{supplier.location}</p>
        ) : null}
        {hq ? (
          <div className='relative overflow-hidden rounded-md border border-border'>
            <div ref={containerRef} className='h-56 w-full sm:h-[360px]' />
            {status === 'loading' ? (
              <div className='absolute inset-0 grid place-items-center bg-muted/40 text-sm text-muted-foreground'>
                Loading map…
              </div>
            ) : null}
            {status === 'unavailable' ? (
              <div className='absolute inset-0 grid place-items-center bg-muted/40 text-sm text-muted-foreground'>
                Map unavailable.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </ProfileSection>
  )
}
